import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "../lib/api";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api
      .get("/analyses")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/auth");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this analysis?")) return;
    try {
      await api.delete(`/analyses/${id}`);
      toast.success("Deleted");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="px-6 pt-6 pb-8" data-testid="dashboard-page">
      <div className="mm-card mb-4">
        <div className="mm-label mb-2">Your dashboard</div>
        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
          Saved analyses
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Every item you analyze is saved here. Tap to revisit prices and the
          generated listing.
        </p>
      </div>

      <Link to="/scan" className="mm-btn-primary mb-4" data-testid="scan-new-btn">
        <Camera size={20} /> Analyze another item
      </Link>

      {loading && <div className="text-sm text-slate-500 py-4" data-testid="dashboard-loading">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="mm-card text-center text-sm text-slate-500" data-testid="dashboard-empty">
          Nothing here yet. Your first analysis will appear once you scan an item.
        </div>
      )}

      <div className="flex flex-col gap-3 mt-2">
        {items.map((it) => (
          <Link
            key={it.id}
            to={`/results/${it.id}`}
            className="mm-card !p-4 flex items-center gap-3"
            data-testid={`dashboard-item-${it.id}`}
          >
            <img
              src={it.image_base64}
              alt="item"
              className="w-16 h-16 rounded-2xl object-cover border border-slate-200 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-900 truncate">
                {it.analysis?.item_name || "Untitled"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {new Date(it.created_at).toLocaleDateString()}
              </div>
              <div className="mt-1 inline-flex items-center gap-2">
                <span className="mm-label text-slate-500">Recommended</span>
                <span className="font-mono font-bold text-blue-700">
                  ${it.analysis?.recommended_price?.toLocaleString?.() || "—"}
                </span>
              </div>
            </div>
            <button
              onClick={(e) => remove(it.id, e)}
              className="w-9 h-9 rounded-full grid place-items-center bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600"
              data-testid={`delete-${it.id}`}
            >
              <Trash2 size={14} />
            </button>
          </Link>
        ))}
      </div>
    </div>
  );
}
